import { useViewSuperblocksV2Context } from "contexts/ViewSuperblocksV2Context";

import {
  ApiTriggerType,
  orgHasFeature,
  OrgFeature,
  OrgVersion,
} from "@superblocksteam/shared";

import { Flag } from "store/slices/featureFlags";
import { useListWorkflowsAndScheduledJobsQuery } from "store/slices/reduxApi/workflowsAndScheduledJobs";

import { useFeatureFlag } from "./useFeatureFlag";

/**
 * Hook to determine workflow visibility and creation capabilities based on:
 * 1. Organization version (V1 vs V2)
 * 2. V2 preview mode (V1 orgs viewing as V2)
 * 3. Feature flag for V2 orgs (org.v2.enable-workflows-for-fetch-credentials)
 * 4. Whether the org has deployed workflows
 *
 * Three scenarios:
 * - V1 org (not viewing V2): full workflow support
 * - V1 org viewing V2: hide workflows on homepage, show on integrations
 * - V2 org: show workflows on homepage (read-only), integrations only with flag + deployed workflows
 */
export function useWorkflowsEnabled() {
  const { effectiveOrgVersion, isOrgV1ViewingV2Transition } =
    useViewSuperblocksV2Context();
  const orgVersion = effectiveOrgVersion;
  const isOrgV2 = orgVersion === OrgVersion.V2;
  const isV1Org = orgHasFeature(orgVersion, OrgFeature.Workflows);

  // V2 orgs can have workflows enabled via feature flag
  const v2WorkflowsEnabled = useFeatureFlag(Flag.V2_ORG_ENABLE_WORKFLOWS);

  const { data: workflowsAndScheduledJobs = [] } =
    useListWorkflowsAndScheduledJobsQuery(undefined, {
      refetchOnMountOrArgChange: false,
    });

  const hasExistingWorkflows = workflowsAndScheduledJobs.some(
    (api) =>
      api?.triggerType === ApiTriggerType.WORKFLOW ||
      api?.actions?.triggerType === ApiTriggerType.WORKFLOW,
  );

  const hasDeployedWorkflows = workflowsAndScheduledJobs.some(
    (api) =>
      (api?.triggerType === ApiTriggerType.WORKFLOW ||
        api?.actions?.triggerType === ApiTriggerType.WORKFLOW) &&
      api?.isDeployed,
  );

  // V1 org viewing V2 preview
  if (isOrgV1ViewingV2Transition) {
    return {
      shouldShowWorkflowsOnHome: false,
      shouldShowWorkflowsOnIntegrations: true,
      canCreateWorkflows: false,
      canMountCreateWorkflowRoute: false,
      canAddWorkflowSteps: false,
      hasExistingWorkflows,
    };
  }

  // V2 org (native, not transitioning)
  if (isOrgV2) {
    return {
      shouldShowWorkflowsOnHome: true,
      shouldShowWorkflowsOnIntegrations:
        v2WorkflowsEnabled && hasDeployedWorkflows,
      canCreateWorkflows: false,
      canMountCreateWorkflowRoute: v2WorkflowsEnabled,
      canAddWorkflowSteps: false,
      hasExistingWorkflows,
    };
  }

  // V1 org (not viewing V2)
  if (isV1Org) {
    return {
      shouldShowWorkflowsOnHome: true,
      shouldShowWorkflowsOnIntegrations: true,
      canCreateWorkflows: true,
      canMountCreateWorkflowRoute: true,
      canAddWorkflowSteps: true,
      hasExistingWorkflows,
    };
  }

  // Fallback: workflows not enabled
  return {
    shouldShowWorkflowsOnHome: false,
    shouldShowWorkflowsOnIntegrations: false,
    canCreateWorkflows: false,
    canMountCreateWorkflowRoute: false,
    canAddWorkflowSteps: false,
    hasExistingWorkflows: false,
  };
}
